/**
 * Clusters / trends — the org's feedback grouped by similarity. Lists clusters
 * (newest-updated first) with their size and summary, a bar to visualize
 * relative volume, and a "recompute" action that re-runs cosine-threshold
 * clustering server-side.
 *
 * Visual: Claude Design "Clusters" screen — each cluster is a card with a
 * grid row (title + sample / volume bar / ticket count). The design's
 * severity-segment and trend-delta columns are omitted gracefully because the
 * real cluster payload carries neither.
 */
import { useState, type ReactElement } from 'react';
import type { ClusterListResponse } from '@hotline/client-core';

import { useAuth } from '../../auth/AuthContext.js';
import { useOrgScope } from '../../org/OrgScopeContext.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Spinner,
} from '../../components/ui/index.js';
import { Icon } from '../../components/ui/icons.js';
import { useAsync, toMessage } from '../../lib/useAsync.js';

export default function ClustersPage(): ReactElement {
  const { client } = useAuth();
  const { query, orgId } = useOrgScope();

  const clusters = useAsync<ClusterListResponse>(
    () => client.api.listClusters(query),
    [client, orgId],
  );
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(null);

  const recompute = async (): Promise<void> => {
    setRecomputing(true);
    setError(null);
    try {
      const result = await client.api.recomputeClusters({ reset: true }, query);
      setLastRun(
        `Processed ${result.sessionsProcessed} sessions → ${result.clusterCount} clusters (threshold ${result.threshold}).`,
      );
      clusters.reload();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setRecomputing(false);
    }
  };

  const items = clusters.data?.items ?? [];
  const maxSize = items.reduce((m, c) => Math.max(m, c.size), 0) || 1;

  return (
    <section>
      <PageHeader
        title="Clusters & trends"
        description="Feedback grouped by similarity, newest activity first."
        actions={
          <Button variant="secondary" onClick={recompute} disabled={recomputing}>
            {recomputing ? (
              <Spinner size="sm" label="Recomputing clusters…" />
            ) : (
              <Icon.refresh />
            )}
            {recomputing ? 'Recomputing…' : 'Recompute'}
          </Button>
        }
      />

      <div className="flex flex-col gap-3">
        {lastRun ? <p className="text-[12.5px] text-muted">{lastRun}</p> : null}
        {error ? <Alert>{error}</Alert> : null}
        {clusters.loading ? <Spinner label="Loading clusters…" /> : null}
        {clusters.error ? <Alert>{clusters.error}</Alert> : null}

        {!clusters.loading && items.length === 0 ? (
          <EmptyState
            title="No clusters yet"
            description="Recompute once you have feedback to group."
          />
        ) : null}

        {items.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {items.map((c) => (
              <li key={c.id} data-cluster-id={c.id}>
                <Card className="overflow-hidden p-0">
                  <div className="grid grid-cols-[1fr_200px_130px] items-center gap-[18px] px-5 py-4 transition-colors hover:bg-surface-hover max-md:grid-cols-1 max-md:gap-2.5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-[9px] text-[14.5px] font-bold text-text">
                        <Icon.clusters
                          size={16}
                          className="shrink-0 text-muted"
                        />
                        <span className="truncate">{c.label}</span>
                      </div>
                      {c.summary ? (
                        <p className="mt-[3px] truncate text-xs text-muted">
                          {c.summary}
                        </p>
                      ) : null}
                    </div>

                    <div className="flex flex-col gap-[5px]">
                      <div className="h-[7px] overflow-hidden rounded-full bg-surface-3">
                        <span
                          className="block h-full rounded-full bg-primary"
                          style={{
                            width: `${Math.round((c.size / maxSize) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div
                      className="flex items-baseline justify-end gap-1.5 tabular-nums max-md:justify-start"
                      aria-label={`${c.size} tickets`}
                    >
                      <span className="text-base font-extrabold text-text">
                        {c.size}
                      </span>
                      <span className="text-xs font-semibold text-faint">
                        tickets
                      </span>
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
